
import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import PageHeader from '@/components/PageHeader';
import ScheduleSection from '@/components/schedule/ScheduleSection';
import { useScrollToSection } from '@/utils/ScrollToSection';
import LogoScroller from '@/components/LogoScroller';

const SchedulePage = () => {
  useScrollToSection();

  useEffect(() => {
    // Check if we have a hash in the URL (e.g., #day2)
    if (window.location.hash) {
      // Remove the # and get the day
      const day = window.location.hash.substring(1);
      
      // Wait a bit for the component to fully render
      setTimeout(() => {
        if (day === 'day2') {
          // For day2, we need to trigger a custom event since it's now a link
          // We'll dispatch a custom event that the DayTabs component can listen to
          window.dispatchEvent(new CustomEvent('setActiveTab', { detail: 'day2' }));
        } else {
          // For day1, find the tab element and click it
          const tabElement = document.querySelector(`[value="${day}"]`) as HTMLElement;
          if (tabElement) {
            tabElement.click();
          }
        }
        
        // Scroll to schedule section
        const scheduleSection = document.getElementById('schedule');
        if (scheduleSection) {
          scheduleSection.scrollIntoView({ behavior: 'smooth' });
        }
      }, 300);
    }
  }, []);

  // Schedule event structured data with all required fields
  const scheduleStructuredData = {
    "@context": "https://schema.org",
    "@type": "Event",
    "name": "AI Conference London 2026",
    "description": "Two days of insightful sessions, workshops, and networking opportunities in AI technology",
    "startDate": "2026-11-25T09:00",
    "endDate": "2026-11-26T17:00",
    "eventStatus": "https://schema.org/EventScheduled",
    "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
    "location": {
      "@type": "Place",
      "name": "The Great Hall",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Hornton St, Kensington",
        "addressLocality": "London",
        "postalCode": "W8 7NX",
        "addressCountry": "GB"
      }
    },
    "offers": {
      "@type": "Offer",
      "url": "https://aiconference.london/tickets",
      "priceCurrency": "GBP",
      "availability": "https://schema.org/InStock"
    },
    "superEvent": {
      "@type": "Event",
      "name": "AI World Congress 2026",
      "url": "https://aiconference.london/"
    }
  };

  return (
    <div className="min-h-screen bg-ai-dark">
      <Helmet>
        <title>Full Agenda 2026 - AI World Congress 2026 - November 25-26, London UK - AI Conference</title>
        <meta name="description" content="Explore our comprehensive two-day schedule featuring keynotes, workshops, and panels from leading AI experts. Join us on November 25-26, 2026 at The Great Hall, London." />
        <link rel="canonical" href="https://aiconference.london/agenda" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://aiconference.london/agenda" />
        <meta property="og:title" content="Conference Schedule - AI World Congress 2026 - November 25-26, London UK - AI Conference" />
        <meta property="og:description" content="Explore our comprehensive two-day schedule featuring keynotes, workshops, and panels from leading AI experts. Join us on November 25-26, 2026 at The Great Hall, London." />
        <meta property="og:image" content="https://aiconference.london/opengraph-image.png" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Conference Schedule - AI World Congress 2026 - November 25-26, London UK - AI Conference" />
        <meta name="twitter:description" content="Explore our comprehensive two-day schedule featuring keynotes, workshops, and panels from leading AI experts. Join us on November 25-26, 2026 at The Great Hall, London." />
        <meta name="twitter:image" content="https://aiconference.london/opengraph-image.png" />

        <script type="application/ld+json">
          {JSON.stringify(scheduleStructuredData)}
        </script>
      </Helmet>
      <Header />
      <PageHeader
        title=""
        backgroundImage="/lovable-uploads/530fddbf-4fea-4198-860f-8165d2f8b445.png"
      />
      
      <div className="relative z-10 -mt-16">
        <div className="relative py-16 md:py-24 overflow-hidden bg-gradient-to-b from-purple-900/80 to-black">
          <div className="absolute inset-0 w-full h-full overflow-hidden z-0">
            <div className="absolute w-full h-[40%] bottom-0 bg-gradient-to-t from-black to-transparent"></div>
            <div className="absolute w-full h-[500px] bottom-0 left-0 right-0">
              <svg className="absolute bottom-0 w-full" viewBox="0 0 1440 320" xmlns="http://www.w3.org/2000/svg">
                <path 
                  fill="#000000" 
                  fillOpacity="1" 
                  d="M0,128L48,138.7C96,149,192,171,288,176C384,181,480,171,576,144C672,117,768,75,864,80C960,85,1056,139,1152,160C1248,181,1344,171,1392,165.3L1440,160L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
                  className="animate-pulse"
                  style={{ animationDuration: '15s' }}
                ></path>
              </svg>
            </div>
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="text-center max-w-4xl mx-auto">
              <h1 className="text-4xl md:text-5xl font-bold mb-8">
                <span className="text-white">Conference </span>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">Schedule 2026</span>
              </h1>
              
              <p className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto animate-fade-in" style={{ animationDelay: '0.2s' }}>
                2026 Dates: November 25-26, London. Explore our comprehensive two-day schedule featuring AI keynotes and panels from leading AI Thought Leaders
              </p>
            </div>
          </div>
        </div>
        <div id="schedule">
          <ScheduleSection />
        </div>
      </div>
      <LogoScroller />
      <Footer />
    </div>
  );
};

export default SchedulePage;
